import type { ReactNode } from 'react'
import { Tooltip } from './Tooltip'

export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  /** Baby-blue-tint bg, steel-blue-3 fg — matches prototype b-blue */
  | 'blue'
  /** Pear-tint bg, olive fg — matches prototype b-pear */
  | 'pear'
  /** Space-cadet bg, pear fg — matches prototype b-navy */
  | 'navy'

interface BadgeProps {
  variant?: BadgeVariant
  children: ReactNode
  /** Optional tooltip shown on hover/focus. Use for approval status details,
   *  e.g. "Approved by Ben Tan, 2 May 14:32". */
  tooltip?: string
  /** Renders a small coloured dot before the label — for status indicators. */
  dot?: boolean
}

const VARIANT_STYLES: Record<BadgeVariant, { bg: string; fg: string; line: string }> = {
  default: {
    bg: 'var(--surface-2)',
    fg: 'var(--text-2)',
    line: 'transparent',
  },
  success: {
    bg: 'var(--success-bg)',
    fg: 'var(--success-fg)',
    line: 'transparent',
  },
  warning: {
    bg: 'var(--warning-bg)',
    fg: 'var(--warning-fg)',
    line: 'transparent',
  },
  danger: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger-fg)',
    line: 'transparent',
  },
  info: {
    bg: 'var(--info-bg)',
    fg: 'var(--info-fg)',
    line: 'transparent',
  },
  blue: {
    bg: 'var(--baby-blue-tint)',
    fg: 'var(--steel-blue-3)',
    line: 'transparent',
  },
  pear: {
    bg: 'var(--pear-tint)',
    fg: '#5C660B',
    line: 'transparent',
  },
  navy: {
    bg: 'var(--space-cadet)',
    fg: 'var(--pear)',
    line: 'transparent',
  },
}

export function Badge({ variant = 'default', children, tooltip, dot }: BadgeProps): JSX.Element {
  const styles = VARIANT_STYLES[variant]
  const chip = (
    <span
      data-variant={variant}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 500,
        lineHeight: 1.4,
        background: styles.bg,
        color: styles.fg,
        border: `1px solid ${styles.line}`,
        whiteSpace: 'nowrap',
        transition:
          'background var(--motion-duration-base) var(--motion-ease-out),' +
          ' border-color var(--motion-duration-base) var(--motion-ease-out),' +
          ' color var(--motion-duration-base) var(--motion-ease-out)',
      }}
    >
      {dot ? (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'currentColor',
            flexShrink: 0,
          }}
        />
      ) : null}
      {children}
    </span>
  )
  if (tooltip) {
    return <Tooltip content={tooltip}>{chip}</Tooltip>
  }
  return chip
}
